import { MoveHorizontalIcon } from "lucide-react";
import { cloneElement, useState } from "react";
import { LinkWithSlug } from "routes";

import {
  FindOneSyncQuery,
  ModelListItemFragment,
  SourceType,
  SyncListItemFragment,
  useModelQuery,
  useRawViewQuery,
  useVerifyUnpublishedChangesQuery,
} from "@/apollo/types";
import { Button, ButtonPrimitive } from "@/components/elements/Button";
import { CronDisplayReadable } from "@/components/elements/CronDisplayReadable";
import { DetailItem } from "@/components/elements/DataBox";
import { FormatRelativeTime } from "@/components/elements/FormatRelativeTime";
import { FormatTime } from "@/components/elements/FormatTime";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import { Skeleton } from "@/components/elements/Skeleton";
import Tooltip from "@/components/elements/Tooltip";
import { useViewDataSourceSlideOver } from "@/components/modules/view-data-source-slideover";
import { WeldFirebaseAppProvider } from "@/features/firebase";
import { formatModelName } from "@/features/models";
import cronExpressionToDayjs from "@/helpers/cronExpressionToDayjs";
import { dateTimeFormat } from "@/helpers/dateFormat";
import syncOperationExplainers from "@/helpers/syncOperationExplainers";
import { IntegrationLogo, IntegrationLogoBox } from "@/integrations";
import { ModelIcon } from "@/pages/ModelTool/components/ModelIcon/ModelIcon";
import { useOrchestrationWorkflow } from "@/pages/Orchestration/components/useWorkflow";
import { useDataWarehouseContext } from "@/providers/DataWarehouseProvider";
import { ArrowRightIcon } from "@heroicons/react/16/solid";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { ChevronRightIcon } from "@heroicons/react/24/outline";

type SyncProps = {
  sync: FindOneSyncQuery["findOneSync"];
};

function Source({ sync }: SyncProps) {
  return (
    <DetailItem title="Source" className="items-end">
      {sync.sourceType === SourceType.RawView ? (
        <SyncRawViewName sync={sync} />
      ) : (
        <SyncModelName sync={sync} />
      )}
    </DetailItem>
  );
}

function SyncRawViewName({ sync }: SyncProps) {
  const { data, loading } = useRawViewQuery({
    variables: { viewId: sync.sourceId },
  });

  const { onOpen: viewSync } = useViewDataSourceSlideOver();
  const dwh = useDataWarehouseContext();

  return (
    <>
      {loading ? (
        <div className="flex h-6 items-center">
          <Skeleton className="h-3 w-44" />
        </div>
      ) : data?.rawView ? (
        <>
          {data.rawView.syncId == null ? (
            <Tooltip content={"This view is not referenced by any sync."}>
              <div className="inline-flex min-w-0 items-center text-sm">
                <IntegrationLogoBox
                  id={dwh.integration.id}
                  className="mr-1 h-5 w-5"
                />
                <span>
                  {data.rawView.schema}.{data.rawView.sourceStream}
                </span>
              </div>
            </Tooltip>
          ) : (
            <Tooltip content={"View data source sync"}>
              <ButtonPrimitive
                onClick={() => {
                  if (!data.rawView?.syncId) return;
                  viewSync({
                    syncId: data.rawView.syncId,
                  });
                }}
                className="inline-flex min-w-0 items-center rounded bg-foreground/5 px-2 py-0.5 text-sm hover:bg-foreground/10 focus:outline-none focus:ring-0"
              >
                <div className="mr-1 flex gap-0.5">
                  <IntegrationLogoBox
                    id={dwh.integration.id}
                    className="h-5 w-5"
                  />
                  {data.rawView.integrationId && (
                    <IntegrationLogoBox
                      id={data.rawView.integrationId}
                      className="h-5 w-5"
                    />
                  )}
                </div>
                <span>
                  {data.rawView.schema}.{data.rawView.sourceStream}
                </span>
              </ButtonPrimitive>
            </Tooltip>
          )}
        </>
      ) : (
        <em className="text-danger">View not found</em>
      )}
    </>
  );
}

function SyncModelName({ sync }: SyncProps) {
  const { data, loading } = useModelQuery({
    variables: { modelId: sync.sourceId },
  });

  return (
    <>
      {loading && (
        <div className="flex h-6 items-center">
          <Skeleton className="h-3 w-44" />
        </div>
      )}
      <WeldFirebaseAppProvider>
        {data && <ModelDisplay model={data.model} />}
      </WeldFirebaseAppProvider>
    </>
  );
}

const ModelDisplay = ({ model }: { model: ModelListItemFragment }) => {
  const { data, loading } = useVerifyUnpublishedChangesQuery({
    fetchPolicy: "network-only",
    variables: { modelId: model.id },
  });
  const hasUnpublishedChanges =
    data?.verifyUnpublishedChanges.hasUnpublishedChanges || false;

  return (
    <Tooltip
      content={
        hasUnpublishedChanges ? (
          <div className="space-y-2">
            <p className="font-medium">
              Unpublished source model changes detected.
            </p>
            <p>
              Make sure to publish your latest changes for them to be used in
              this sync.
            </p>
          </div>
        ) : (
          "View model in the Transform tool"
        )
      }
    >
      <LinkWithSlug
        to={`/editor/${model.id}`}
        className="inline-flex min-w-0 items-center space-x-2 rounded bg-foreground/5 px-2 py-0.5 text-sm hover:bg-foreground/10"
      >
        {loading ? (
          <LoadingSpinner className="mr-1.5 h-4 w-4" />
        ) : hasUnpublishedChanges ? (
          <ExclamationTriangleIcon className="mr-1.5 mt-px h-4 w-4 text-yellow-500" />
        ) : (
          <ModelIcon model={model} className="mr-1.5" />
        )}
        {formatModelName(model)}
      </LinkWithSlug>
    </Tooltip>
  );
};

const IntegrationSpecificFields = (props: {
  rEltSync: SyncListItemFragment | FindOneSyncQuery["findOneSync"];
}) => {
  switch (props.rEltSync.destinationIntegrationId) {
    case "google-sheets":
    case "google-sheets-service-account":
      return (
        <>
          <ChevronRightIcon className="h-4 w-4" />
          <span>
            {props.rEltSync.config?.destinationSettings?.spreadsheet?.label} (
            {props.rEltSync.config?.destinationSettings?.sheet?.label})
          </span>
        </>
      );
    default:
      return null;
  }
};

function RevEltDisplayNameSmall(props: {
  rEltSync: FindOneSyncQuery["findOneSync"];
}) {
  return (
    <div className="flex items-center space-x-2">
      <IntegrationLogo
        id={props.rEltSync.destinationIntegrationId}
        className="h-5"
      />
      <div className="flex items-center space-x-1 text-sm text-gray-800 dark:text-white">
        <div>
          {props.rEltSync.destinationConnection.label ||
            props.rEltSync.destinationIntegrationId}
        </div>
        <ChevronRightIcon className="h-3 w-3" />
        <div className="">{props.rEltSync.primaryObject}</div>
        {props.rEltSync.config?.destinationSettings?.scopeId && (
          <>
            <ChevronRightIcon className="h-3 w-3" />
            <div className="">
              {props.rEltSync.config?.destinationSettings?.scopeId}
            </div>
          </>
        )}
        <IntegrationSpecificFields rEltSync={props.rEltSync} />
      </div>
    </div>
  );
}

function SyncConnector({ sync }: SyncProps) {
  return (
    <DetailItem title="Connector">
      <div className="flex items-center space-x-1">
        <LinkWithSlug
          to={`/settings/connections/${sync.destinationConnectionId}`}
        >
          <RevEltDisplayNameSmall rEltSync={sync} />
        </LinkWithSlug>
      </div>
    </DetailItem>
  );
}

function SyncSchedule({ sync }: SyncProps) {
  if (sync.schedulerType === "local") {
    return <SyncLocalScheduler sync={sync} />;
  }
  if (sync.schedulerType === "global" && sync.orchestrationWorkflow) {
    return <OrchestrationDetails workflowId={sync.orchestrationWorkflow.id} />;
  }
  return null;
}

function SyncLocalScheduler({ sync }: SyncProps) {
  return (
    <DetailItem title="Frequency">
      {sync.syncInterval ? (
        <Tooltip
          content={
            <span>
              Next sync:{" "}
              <FormatTime date={cronExpressionToDayjs(sync.syncInterval)} />
            </span>
          }
        >
          <div className="cursor-help">
            <CronDisplayReadable cron={sync.syncInterval} />{" "}
            <span className="text-muted-foreground">
              (
              <FormatRelativeTime
                date={cronExpressionToDayjs(sync.syncInterval)}
              />
              )
            </span>
          </div>
        </Tooltip>
      ) : (
        "Not defined"
      )}
    </DetailItem>
  );
}

const OrchestrationDetails = (props: { workflowId: string }) => {
  const { workflow, loading } = useOrchestrationWorkflow(props.workflowId);
  return (
    <>
      <div className="">
        <DetailItem title="Frequency">
          <div>
            {workflow?.cronExpression ? (
              <Tooltip
                content={`Next run: ${cronExpressionToDayjs(
                  workflow.cronExpression,
                ).format(dateTimeFormat)}`}
              >
                <div className="cursor-help">
                  <CronDisplayReadable cron={workflow.cronExpression} />{" "}
                  <span className="text-muted-foreground">
                    (
                    <FormatRelativeTime
                      date={cronExpressionToDayjs(workflow.cronExpression)}
                    />
                    )
                  </span>
                </div>
              </Tooltip>
            ) : loading ? (
              <LoadingSpinner />
            ) : (
              "Not defined"
            )}
          </div>
        </DetailItem>
      </div>
      <div className="">
        <DetailItem title="Orchestration workflow">
          {workflow ? (
            <LinkWithSlug
              className="underline"
              to={`/orchestration/${workflow.id}`}
            >
              {workflow.name}
            </LinkWithSlug>
          ) : loading ? (
            <LoadingSpinner />
          ) : (
            "Not defined"
          )}
        </DetailItem>
      </div>
    </>
  );
};

function SyncOperation({ sync }: SyncProps) {
  const operation = sync?.operations?.[0];
  return (
    <DetailItem title="Operation">
      <Tooltip
        content={syncOperationExplainers(operation?.operationMode ?? "")}
      >
        <span className="capitalize">{operation?.operationMode}</span>
      </Tooltip>
    </DetailItem>
  );
}

function SyncMappings({ sync }: SyncProps) {
  const LIMIT = 5;
  const [showAll, setShowAll] = useState(false);

  const primaryMappings = sync.mappings.filter((x) => x.primaryKey);
  const fieldMappings = sync.mappings.filter((x) => !x.primaryKey);

  const mappingsToShow = showAll
    ? fieldMappings
    : fieldMappings.slice(0, LIMIT);
  const hasMoreMappings = mappingsToShow.length < fieldMappings.length;

  return (
    <>
      {primaryMappings.length > 0 && (
        <DetailItem title="Identifiers">
          {primaryMappings.map((item) => {
            return (
              <MappingItem
                key={item.id}
                from={item.sourcePropertyName}
                to={item.destinationPropertyName}
                symbol={<MoveHorizontalIcon />}
              />
            );
          })}
        </DetailItem>
      )}
      <DetailItem title="Mappings" className="items-start">
        <div className="space-y-2">
          {mappingsToShow.map((item, i, arr) => {
            if (i === arr.length - 1) {
              return (
                <div key={item.id} className="flex items-center">
                  <MappingItem
                    key={item.id}
                    from={item.sourcePropertyName}
                    to={item.destinationPropertyName}
                    symbol={<ArrowRightIcon />}
                  />
                  <div className="flex items-center px-2">
                    {hasMoreMappings && (
                      <Button
                        variant="link"
                        size="xs"
                        onClick={() => setShowAll(true)}
                      >
                        Show all mappings ({fieldMappings.length})
                      </Button>
                    )}
                    {showAll && fieldMappings.length > LIMIT && (
                      <Button
                        variant="link"
                        size="xs"
                        onClick={() => setShowAll(false)}
                      >
                        Show less
                      </Button>
                    )}
                  </div>
                </div>
              );
            }
            return (
              <MappingItem
                key={item.id}
                from={item.sourcePropertyName}
                to={item.destinationPropertyName}
                symbol={<ArrowRightIcon />}
              />
            );
          })}
        </div>
      </DetailItem>
    </>
  );
}

const SyncDetails = {
  Source: Source,
  Connector: SyncConnector,
  Schedule: SyncSchedule,
  Operation: SyncOperation,
  Mappings: SyncMappings,
};

export default SyncDetails;

const MappingItem = ({
  from,
  to,
  symbol,
}: {
  from: string | undefined;
  to: string | undefined;
  symbol: React.ReactNode;
}) => (
  <div className="flex">
    <div className="flex min-w-0 items-center space-x-2 rounded bg-gray-100 px-2 py-0.5 text-sm text-gray-700 dark:bg-gray-700 dark:text-white">
      <Tooltip content={from || ""}>
        <span className="truncate" style={{ minWidth: "10px" }}>
          {from}
        </span>
      </Tooltip>
      {cloneElement(symbol as React.ReactElement, {
        className: "h-3 w-3 text-gray-500 dark:text-white",
      })}
      <Tooltip content={to || ""}>
        <span className="truncate" style={{ minWidth: "10px" }}>
          {to}
        </span>
      </Tooltip>
    </div>
  </div>
);
